import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { required, requiredIf } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { mapState } from "pinia";
import { useApp } from "../../../../App/useApp";
import moment from "moment";

import { IAgencyServices } from "../../../../../resources-vue/vue-interfaces/i-agency";
import { ApplicationEnum } from "../../../../../enums/ApplicationEnum";
import { IGetProviderItem } from "../../../../../resources-vue/vue-interfaces/i-providers-service";
import { INonAdmitReasonQuery, INonAdmitReasonsListItem } from "../../../../../resources-vue/vue-interfaces/i-branch";
import { ProvidersService } from "../../../../../services/providers-service";
import { BranchesService } from "../../../../../services/branches-service";
import config from "../../../../../common/config";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { ParentPermissionEnum } from "../../../../../enums/ParentPermissionEnum";
import { PermissionActionEnum } from "../../../../../enums/PermissionActionEnum";
import { ICheckPermissions } from "../../../../../resources-vue/vue-interfaces/i-permissions";
import { CentralIntakeService } from "../../../../../services/central-intake.service";
import { ToastrService } from "../../../../../services/toastr.service";
import { ICreateInterimReferralPayload } from "../../../../../resources-vue/vue-interfaces/i-get-referral";
import { LineOfServices } from "../../../../../enums/enums";
import { ReferralService } from "../../../../../services/referral-service";

import FormSection from "../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import DatePicker from "../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";

export default defineComponent({
    components: {
        FormSection,
        DatePicker
    },

    props: {
        referral: {
            type: Object
        },
        isTransferRequested: {
            type: Boolean
        }
    },

    validations() {
        return {
            referTo: { required },
            selectedBranch: { required: requiredIf(this.referTo !== null) },
            nonAdmitDate: { required: requiredIf(this.isNonAdmit === true) },
            selectedNonAdmitReason: { required: requiredIf(this.isNonAdmit == true) }
        };
    },

    data(vm) {
        return {
            _toastrService: null,
            _centralIntakeService: null,
            _permissionManager: null,
            _providersService: null,
            _branchesService: null,
            isTransferInProgress: false,
            isLoading: false,
            isNonAdmit: false,
            referTo: null as IAgencyServices,
            providerList: [],
            allProviderList: [],
            allBranchList: [],
            referableAgencyServices: null,
            agencyServices: [
                {
                    name: "Hospice",
                    show: false,
                    id: ApplicationEnum.AxxessHospiceFE,
                    isNonMedical: false
                },
                {
                    name: "Palliative",
                    show: false,
                    id: ApplicationEnum.AxxessPalliative,
                    isNonMedical: false
                },
                {
                    name: "Home Health",
                    show: false,
                    id: ApplicationEnum.AgencyCore,
                    isNonMedical: false
                },
                {
                    name: "Home Care (Medical)",
                    show: false,
                    id: ApplicationEnum.HomeCare,
                    isNonMedical: false
                },
                {
                    name: "Home Care (Non-Medical)",
                    show: false,
                    id: ApplicationEnum.HomeCare,
                    isNonMedical: true
                }
            ],
            checkProductPermission: {} as ICheckPermissions,
            selectedBranch: null,
            nonAdmitReasons: [],
            selectedNonAdmitReason: "",
            nonAdmitDate: "",
            lineOfServiceEnumMap: LineOfServices,
            locationId: null,
            accountId: null,
            nonAdmitReason: "",
            nonAdmitReasonId: null,
            _referralService: null,
            v$: useVuelidate()
        };
    },

    computed: {
        ...mapState(useApp, ["organizations"])
    },

    emits: ["update:isTransferRequested"],

    async created() {
        this._toastrService = Container.instance.get(ToastrService);
        this._centralIntakeService = Container.instance.get(CentralIntakeService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._providersService = Container.instance.get(ProvidersService);
        this._branchesService = Container.instance.get(BranchesService);
        this._referralService = Container.instance.get(ReferralService);
        const organizationId = localStorage.getItem(config.organizationId);
        const selectOrganization = this.organizations.filter((organization) => organization.id == organizationId);
        const selectedAgencies = selectOrganization[0].agencies;
        const agencyList = selectedAgencies.map((agency) => {
            let agencyAndApplicationIds = agency.id.split("_");
            let application = parseInt(agencyAndApplicationIds[1], 10);
            return {
                ...agency,
                application: application
            };
        });
        const showNewReferralHos = agencyList.some((el: any) => el.application === ApplicationEnum.AxxessHospiceFE);
        const showNewReferralPal = agencyList.some((el: any) => el.application === ApplicationEnum.AxxessPalliative);
        const showNewReferralHc = agencyList.some((el: any) => el.application === ApplicationEnum.HomeCare);
        const showNewReferralHh = agencyList.some((el: any) => el.application === ApplicationEnum.AgencyCore);
        const permissionChecker = (Id: number, ParentPermission: any, Action: any, showReferral: boolean) => {
            return this._permissionManager.checkPermissionByProduct(ParentPermission, Action, Id) && showReferral;
        };
        this.checkProductPermission = {
            showNewReferralHospice: permissionChecker(
                ApplicationEnum.AxxessHospiceFE,
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canAdd,
                showNewReferralHos
            ),
            showNewReferralPalliative: permissionChecker(
                ApplicationEnum.AxxessPalliative,
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canAdd,
                showNewReferralPal
            ),
            showNewReferralHomeHealth: permissionChecker(
                ApplicationEnum.AgencyCore,
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canAdd,
                showNewReferralHh
            ),
            showNewReferralHomeCare: permissionChecker(
                ApplicationEnum.HomeCare,
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canAdd,
                showNewReferralHc
            )
        };
        this.referableAgencyServices = this.agencyServices.filter((agency) => {
            if (agency.id === ApplicationEnum.AxxessHospiceFE) {
                agency.show = this.checkProductPermission.showNewReferralHospice;
            }
            if (agency.id === ApplicationEnum.AxxessPalliative) {
                agency.show = this.checkProductPermission.showNewReferralPalliative;
            }
            if (agency.id === ApplicationEnum.HomeCare) {
                agency.show = this.checkProductPermission.showNewReferralHomeCare;
            }
            if (agency.id === ApplicationEnum.AgencyCore) {
                agency.show = this.checkProductPermission.showNewReferralHomeHealth;
            }
            return agency.id !== this.referral.application;
        });
        const providers = await this._providersService.getProvider();
        this.allBranchList = await this._branchesService.getAllBranches();
        this.allProviderList = providers.filter((provider: any) => provider.branches.length > 0);
        this.getNonAdmitReasons();
    },

    watch: {
        selectedBranch(newValue: any) {
            if (!newValue) {
                this.locationId = "";
                return;
            }
            this.locationId = this.selectedBranch.id;
            this.accountId = this.selectedBranch.accountId;
        },
        selectedNonAdmitReason(newValue: any) {
            if (newValue) {
                this.nonAdmitReasonId = newValue.value;
                this.nonAdmitReason = newValue.name;
            } else {
                this.nonAdmitReasonId = null;
                this.nonAdmitReason = "";
            }
        }
    },

    methods: {
        updateBranches() {
            if (this.referTo) {
                this.providerList = [];
                let providers = {} as any;
                let selectedBranches = this.allBranchList.filter((branch) => branch.application === this.referTo.id);
                selectedBranches.forEach((branch) => {
                    providers[`${branch.providerId}_${branch.providerName}_${branch.application}`] = {};
                });
                for (const everyProvider of Object.keys(providers)) {
                    let obj: IGetProviderItem = { id: null, name: null, branches: null };
                    const selectedProviderBranches = selectedBranches.filter(
                        (branch) =>
                            branch.providerId == everyProvider.split("_")[0] &&
                            everyProvider.split("_")[2] == branch.application
                    );
                    obj.id = everyProvider.split("_")[0];
                    obj.name = everyProvider.split("_")[1];
                    obj.branches = selectedProviderBranches;
                    this.providerList.push(obj);
                }
            } else {
                this.selectedBranch = null;
            }
        },
        async getNonAdmitReasons() {
            let providerIds: string[] = [];
            this.allBranchList.forEach((branch: any) => {
                if (branch.accountId == this.referral.accountId && !providerIds?.includes(branch.providerId)) {
                    providerIds.push(branch.providerId);
                }
            });
            const objQuery: INonAdmitReasonQuery = {
                accountId: this.referral.accountId,
                providerIds: providerIds,
                application: this.referral.application
            };
            const nonAdmitReasonsList = await this._centralIntakeService.getNonAdmitReasons(objQuery);
            if (nonAdmitReasonsList.data) {
                this.nonAdmitReasons = nonAdmitReasonsList.data?.map((reason: INonAdmitReasonsListItem) => ({
                    value: reason.id,
                    name: reason.reason,
                    commentRequired: reason.isCommentRequired || false
                }));
            }
        },
        updateNonAdmitDate(date: string) {
            this.nonAdmitDate = date;
        },
        closeNonAdmitForm() {
            this.$emit("update:isTransferRequested", false);
        },
        async convertToRefer() {
            const validated = await this.v$.$validate();
            if (validated) {
                const isRefer = true;
                this.nonAdmitDate = moment(this.nonAdmitDate, "MM/DD/YYYY").format("MM-DD-YYYY");
                if (
                    this.referTo.id == ApplicationEnum.AxxessHospiceFE ||
                    this.referTo.id == ApplicationEnum.AxxessPalliative
                ) {
                    let lineOfService: number;
                    if (this.referTo.id == ApplicationEnum.AxxessHospiceFE) {
                        lineOfService = this.lineOfServiceEnumMap.enum.Hospice;
                    } else {
                        lineOfService = this.lineOfServiceEnumMap.enum.PalliativeCare;
                    }
                    const payload: ICreateInterimReferralPayload = {
                        accountId: this.accountId,
                        agencyId: this.accountId,
                        application: this.referTo.id,
                        firstName: this.referral.firstName,
                        lastName: this.referral.lastName,
                        lineOfService: lineOfService,
                        locationId: this.locationId
                    };
                    const response = await this._referralService.createInterimReferral(payload);
                    let referralId = response.referralId;
                    let generatedPatientId = response.patientId;
                    if (this.isNonAdmit) {
                        this.$router.push(
                            `/axxess-central/create/referral?id=${referralId}&accountId=${this.accountId}&application=${
                                this.referTo.id
                            }&isRefer=${isRefer}&isNonAdmit=${this.isNonAdmit}&nonAdmitReason=${
                                this.nonAdmitReason
                            }&nonAdmitReasonId=${this.nonAdmitReasonId}&nonAdmitDate=${this.nonAdmitDate}&patientId=${
                                this.referral.id
                            }&patientApplication=${this.referral.application}&patientBranchId=${
                                this.referral.accountId
                            }&newPatientId=${generatedPatientId}&locationId=${
                                this.locationId
                            }&isPatient=${false}&isCreate=true`
                        );
                    } else {
                        this.$router.push(
                            `/axxess-central/create/referral?id=${referralId}&accountId=${this.accountId}&application=${this.referTo.id}&isRefer=${isRefer}&isNonAdmit=${this.isNonAdmit}&patientId=${this.referral.id}&patientApplication=${this.referral.application}&patientBranchId=${this.referral.accountId}&newPatientId=${generatedPatientId}&locationId=${this.locationId}&isPatient=${this.referral.isPatient}&isCreate=true`
                        );
                    }
                } else if (this.referTo.id == ApplicationEnum.AgencyCore) {
                    if (this.isNonAdmit) {
                        this.$router.push(
                            `/axxess-central/create/homehealth-referral?id=0000000-0000-0000-0000-0000000000000&accountId=${
                                this.accountId
                            }&application=${this.referTo.id}&isCreate=true&isRefer=${isRefer}&isNonAdmit=${
                                this.isNonAdmit
                            }&nonAdmitReason=${this.nonAdmitReason}&nonAdmitReasonId=${
                                this.nonAdmitReasonId
                            }&nonAdmitDate=${this.nonAdmitDate}&patientId=${this.referral.id}&patientApplication=${
                                this.referral.application
                            }&patientBranchId=${this.referral.accountId}&locationId=${
                                this.locationId
                            }&isPatient=${false}`
                        );
                    } else {
                        this.$router.push(
                            `/axxess-central/create/homehealth-referral?id=0000000-0000-0000-0000-0000000000000&accountId=${
                                this.accountId
                            }&application=${this.referTo.id}&isCreate=true&isRefer=${isRefer}&isNonAdmit=${
                                this.isNonAdmit
                            }&patientId=${this.referral.id}&patientApplication=${
                                this.referral.application
                            }&patientBranchId=${this.referral.accountId}&locationId=${
                                this.locationId
                            }&isPatient=${false}`
                        );
                    }
                } else if (this.referTo.id == ApplicationEnum.HomeCare) {
                    if (this.isNonAdmit) {
                        this.$router.push({
                            path: "/axxess-central/create/homecare-referral",
                            query: {
                                accountId: this.accountId,
                                isNonMedical: String(this.referTo.isNonMedical),
                                isCreate: "true",
                                locationId: this.locationId,
                                isRefer: String(isRefer),
                                isNonAdmit: String(this.isNonAdmit),
                                patientId: this.referral.id,
                                patientApplication: this.referral.application,
                                patientAgencyId: this.referral.accountId,
                                patientAgencyName: this.referral.agencyName,
                                nonAdmitReason: this.nonAdmitReason,
                                nonAdmitReasonId: this.nonAdmitReasonId,
                                nonAdmitDate: this.nonAdmitDate,
                                isPatient: String(false)
                            }
                        });
                    } else {
                        this.$router.push({
                            path: "/axxess-central/create/homecare-referral",
                            query: {
                                accountId: this.accountId,
                                isNonMedical: String(this.referTo.isNonMedical),
                                isCreate: "true",
                                locationId: this.locationId,
                                isRefer: String(isRefer),
                                isNonAdmit: String(this.isNonAdmit),
                                patientId: this.referral.id,
                                patientApplication: this.referral.application,
                                patientAgencyId: this.referral.accountId,
                                patientAgencyName: this.referral.agencyName,
                                isPatient: String(false)
                            }
                        });
                    }
                } else {
                    this.$router.push(
                        `/axxess-central/create/homehealth-referral?id=0000000-0000-0000-0000-0000000000000&accountId=${this.accountId}&application=${this.referTo.id}&isCreate=true`
                    );
                }
            } else {
                this._toastrService.error({
                    title: `Validation Error`,
                    message: "Please fill required fields"
                });
            }
        }
    }
});
